import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CustomModal from '../../CustomModal'
import { selectIsNeededZIndex } from '../../../../../redux/selector/system'
import { ButtonsModalWrapper, InputReasonWrapper } from './index.styled'
import { useTranslation } from 'react-i18next'
import KeyBindings from '../../../keybindings'
import ErrorMessage from '../../../message/ErrorMessage'
import ModalButton from '../../ButtonsModal/ModalButton'
import Button from '../../../buttons/Button'
import { FormButtons } from '../../../form/index.styled'
import TextAreaInputControlled from '../../../inputs/TextAreaInput/controlled'
import { reasonAbortOrder } from './constants'

const EVENT_Z_INDEX = 126

const AbortOrderModal = React.memo(({ isOpen, close, onAbortOrder }) => {
	const { t } = useTranslation()
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const inputRef = useRef(null)
	const [activeButtonIndex, setActiveButtonIndex] = useState(0)
	const [abortReason, setAbortReason] = useState('')
	const [displayError, setDisplayError] = useState(false)

	const buttons = reasonAbortOrder?.map(reason =>
		({ icon: 'cancel', value: reason?.value, text: t(reason?.label), onClick: e => handleItemClick(e, reason.value), show: true }))

	useEffect(() => {
		if (isOpen) {
			setActiveButtonIndex(0)
		}
	}, [isOpen])

	const focusInput = useCallback((reason) => {
		if (reason === 'OTHER' && inputRef.current) {
			inputRef.current.focus()
		} else {
			setAbortReason('')
			setDisplayError(false)
			if (inputRef.current) {
				inputRef.current.blur()
			}
		}
	}, [inputRef])

	useEffect(() => {
		const reason = buttons[activeButtonIndex]?.value
		focusInput(reason)
	}, [focusInput, activeButtonIndex, buttons])

	const handleItemClick = useCallback((event, reason) => {
		if (event) event.preventDefault()
		focusInput(reason)
	}, [focusInput])

	const handleArrowUp = useCallback(() => {
		if (!buttons[activeButtonIndex - 1]) {
			setActiveButtonIndex(buttons.length - 1)
		} else {
			setActiveButtonIndex(activeButtonIndex - 1)
		}
	}, [buttons, activeButtonIndex])

	const handleArrowDown = useCallback(() => {
		if (!buttons[activeButtonIndex + 1]) {
			setActiveButtonIndex(0)
		} else {
			setActiveButtonIndex(activeButtonIndex + 1)
		}
	}, [buttons, activeButtonIndex])

	const handleButtonClick = useCallback((e, button, index) => {
		setActiveButtonIndex(index)
		button?.onClick(e)
	}, [])

	const handleChangeReason = event => {
		setDisplayError(false)
		setAbortReason(event.target.value)
	}

	const handleInputFocus = useCallback(() => {
		if (buttons[activeButtonIndex].value !== 'OTHER') {
			setActiveButtonIndex(2)
		}
	}, [buttons, activeButtonIndex])

	const handleClose = useCallback(() => {
		setDisplayError(false)
		setAbortReason('')
		close()
	}, [close])

	const handleSaveReason = useCallback(async () => {
		const selectedBtn = buttons[activeButtonIndex]
		const trimmedReason = abortReason.trim()
		if (!trimmedReason && selectedBtn.value === 'OTHER') {
			setDisplayError(true)
			return
		}
		onAbortOrder(trimmedReason, selectedBtn.value)
	}, [activeButtonIndex, buttons, abortReason, onAbortOrder])

	return (
		<>
			<CustomModal isOpen={isOpen}
									 close={handleClose}
									 size="small"
									 title={t('order.modal.abortOrder.title')}
									 id="cancel-reason-modal"
									 zIndex={EVENT_Z_INDEX}
									 disableBackButton={true}
									 returnZIndex={0}>
				<ButtonsModalWrapper>
					{buttons?.map((button, index, arr) => {
						const className = 'thinBorder ' + (index === arr.length - 1 ? 'noBottomRadius ' : '')
						return <ModalButton key={index}
																index={index}
																tabIndex={index}
																button={button}
																onClick={handleButtonClick}
																isActive={index === activeButtonIndex}
																className={className} />
					})}
				</ButtonsModalWrapper>
				<InputReasonWrapper>
					<TextAreaInputControlled name="reason"
																	 type="text"
																	 refInput={inputRef}
																	 onChange={handleChangeReason}
																	 onFocus={handleInputFocus}
																	 value={abortReason}
																	 placeholder={t('order.modal.abortOrder.placeholder')}
																	 autoComplete="off"
																	 className="lf-border"
					/>
					{displayError && <ErrorMessage className="textLeft">{t('order.modal.abortOrder.error')}</ErrorMessage>}
				</InputReasonWrapper>
				<FormButtons>
					<Button icon="clear" keyButton="F3" text={t('buttons.controls.cancel')}
									zIndex={EVENT_Z_INDEX} onClick={handleClose} className="back" />
					<Button color="green" icon="checkmark" keyButton="F2" text={t('buttons.controls.ok')}
									zIndex={EVENT_Z_INDEX} onClick={handleSaveReason} testId="button-submit" />
				</FormButtons>

			</CustomModal>

			{isNeededZIndex &&
				<KeyBindings arrowUp={handleArrowUp}
										 arrowDown={handleArrowDown} />}
		</>
	)
})

AbortOrderModal.displayName = 'AbortOrderModal'
export default AbortOrderModal
